import React from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
} from "@mui/material";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { leavePalette, typeMeta, statusMeta, appliedOn } from "./leaveHelpers";
import { APP_BAR_OFFSET, designShell } from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";

// Default columns for callers that render the table without a saved-view
// `headers` prop (e.g. the My Leaves detail screen/drawer), so it keeps the
// same seven columns it always showed. The Leave screen passes its own
// SavedViewSelector `visibleHeaders` instead.
const DEFAULT_LEAVE_HEADERS = [
  { name: "Leave Type", key: "leave_type" },
  { name: "From", key: "from_date" },
  { name: "To", key: "to_date" },
  { name: "Days", key: "business_days" },
  { name: "Reason", key: "reason" },
  { name: "Status", key: "status" },
  { name: "Applied On", key: "applied" },
];

// Redesigned Leave History table. Presentational only — it renders whatever
// already-filtered/paginated rows it's handed against the active saved-view
// columns (`headers`) and defers the actual leave details to the existing
// ViewLeave drawer via onRowClick, so no approval/edit logic moved here.
const headCellSx = {
  ...sharedTableHeadCellSx,
};

const bodyCellSx = {
  ...sharedTableBodyCellSx,
};

const dash = (v) => (v === 0 || v ? v : "—");

// Per-column cell content — keeps the rich Leave Type / Status cells while
// every other column falls back to its plain normalized value.
const renderLeaveCell = (row, head) => {
  switch (head.key) {
    case "leave_type": {
      const meta = typeMeta(row.leave_type);
      const Icon = meta.icon;
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${meta.color}1A`,
              color: meta.color,
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
          </Box>
          <Typography fontWeight={600} sx={{ fontSize: 13 }}>
            {row.leave_type || "—"}
          </Typography>
        </Stack>
      );
    }
    case "status": {
      const color = statusMeta(row.status, row.color_code);
      return (
        <Box
          sx={{
            display: "inline-block",
            px: 1.2,
            py: 0.35,
            borderRadius: 5,
            fontSize: 12,
            fontWeight: 700,
            color,
            bgcolor: `${color}1A`,
          }}
        >
          {row.status || "—"}
        </Box>
      );
    }
    case "reason": {
      const reasonText = row.reason || row.applied_reason || row.description || "—";
      return (
        <Tooltip
          title={reasonText === "—" ? "" : reasonText}
          arrow
          placement="top"
          slotProps={tooltipStyleProps}
        >
          <Box
            sx={{
              maxWidth: 220,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: reasonText === "—" ? "default" : "help",
            }}
          >
            {reasonText}
          </Box>
        </Tooltip>
      );
    }
    case "applied":
      return (
        <Typography
          component="span"
          sx={{ fontSize: 13, color: leavePalette.subText, whiteSpace: "nowrap" }}
        >
          {dash(row.applied || appliedOn(row))}
        </Typography>
      );
    default:
      return dash(row[head.key]);
  }
};

const LeaveHistoryMobileCard = ({ row, onClick }) => {
  const meta = typeMeta(row.leave_type);
  const Icon = meta.icon;
  const statusColor = statusMeta(row.status, row.color_code);

  return (
    <Box
      onClick={() => onClick(row)}
      sx={{
        p: 1.5,
        border: `1px solid ${leavePalette.border}`,
        borderRadius: 2,
        backgroundColor: "#FFFFFF",
        cursor: "pointer",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${meta.color}1A`,
            color: meta.color,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography
              fontWeight={700}
              sx={{ fontSize: 13.5, color: leavePalette.text, minWidth: 0 }}
            >
              {row.leave_type || "-"}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 5,
                fontSize: 11.5,
                fontWeight: 700,
                color: statusColor,
                bgcolor: `${statusColor}1A`,
                flexShrink: 0,
              }}
            >
              {row.status || "-"}
            </Box>
          </Stack>

          <Typography
            sx={{ mt: 0.75, fontSize: 12.5, color: leavePalette.subText }}
          >
            {dash(row.from_date)} - {dash(row.to_date)} - {dash(row.business_days)} day
            {Number(row.business_days) === 1 ? "" : "s"}
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontSize: 13,
              color: leavePalette.text,
              overflowWrap: "anywhere",
            }}
          >
            {row.reason || row.applied_reason || row.description || "-"}
          </Typography>
          <Typography
            sx={{ mt: 0.75, fontSize: 12, color: leavePalette.subText }}
          >
            Applied On: {dash(row.applied || appliedOn(row))}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export const LeaveHistoryTable = ({
  headers = DEFAULT_LEAVE_HEADERS,
  rows,
  onRowClick,
  colSpan,
  // The desktop table needs 720px+ to lay out its 7 columns; the xs/sm split
  // below is a viewport-width media query, so it doesn't know when this table
  // is instead sitting inside a ~620px side panel (LeaveDetailsDrawer's
  // History tab) on an otherwise-desktop-width screen -- there the media
  // query still picks the desktop table, which then has to horizontally
  // scroll inside the drawer and reads as "broken". `compact` lets a
  // container-constrained caller force the mobile card list regardless of
  // viewport width.
  compact = false,
  // Distance from the viewport top at which the header should stick: the
  // app bar plus the sticky filter bar above this table (measured by caller).
  stickyTop = APP_BAR_OFFSET,
  // Height of the sticky footer below the table, reserved as bottom padding
  // so the last row never hides behind it.
  footerHeight = 0,
}) => {
  const activeHeaders = headers.length ? headers : DEFAULT_LEAVE_HEADERS;
  const totalCols = activeHeaders.length;
  const mobileDisplay = compact ? "flex" : { xs: "flex", sm: "none" };
  const desktopDisplay = compact ? "none" : { xs: "none", sm: "block" };
  return (
    <Box>
      <Stack spacing={1.25} sx={{ display: mobileDisplay, p: 2 }}>
        {rows.length === 0 ? (
          <Box
            sx={{
              py: 3,
              textAlign: "center",
              color: leavePalette.subText,
              fontSize: 13,
            }}
          >
            No leave requests found.
          </Box>
        ) : (
          rows.map((row, idx) => (
            <LeaveHistoryMobileCard
              key={row.id ?? idx}
              row={row}
              onClick={onRowClick}
            />
          ))
        )}
      </Stack>
      {/* Same structure as the Team Workspace Leave list: a header-only table
          pinned in a sticky wrapper, with the body table beneath it. */}
      <Box
        sx={{
          display: desktopDisplay,
          position: "sticky",
          top: stickyTop,
          zIndex: 2,
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Table size="small" sx={{ minWidth: 720, width: "100%", tableLayout: "fixed" }}>
          <TableHead sx={designShell.tableStickyHead}>
            <TableRow sx={designShell.tableStickyHeadRow}>
              {activeHeaders.map((head) => (
                <TableCell
                  key={head.key}
                  sx={{
                    ...headCellSx,
                    position: "static",
                    top: "auto",
                    // The Leave Type body cell leads with a 30px icon + 8px
                    // gap, pushing its text right; indent this header by the
                    // same amount so the label sits over the name.
                    ...(head.key === "leave_type" ? { pl: "54px" } : {}),
                  }}
                >
                  {head.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </Table>
      </Box>
      <TableContainer
        sx={{
          display: desktopDisplay,
          // overflow stays visible on sm+ so the sticky header above keeps
          // pinning to the page scroll (same rule as the Team Workspace list).
          overflowX: { xs: "auto", sm: "visible" },
          overflowY: { xs: "auto", sm: "visible" },
          pb: footerHeight ? `${footerHeight}px` : 0,
        }}
      >
        <Table size="small" sx={{ minWidth: 720, width: "100%", tableLayout: "fixed" }}>
          <TableBody>
            {rows.length === 0 ? (
              <TableEmptyState
                colSpan={colSpan || totalCols}
                dataLength={rows}
                title="No Leave Records Found"
                subtitle="There's nothing to show here yet."
              />
            ) : (
              rows.map((row, idx) => (
                <TableRow
                  key={row.id ?? idx}
                  hover
                  sx={{
                    cursor: "pointer",
                    ...designShell.tableRowHover,
                    "&:last-child td": { borderBottom: "none" },
                  }}
                  onClick={() => onRowClick(row)}
                >
                  {activeHeaders.map((head) => (
                    <TableCell key={head.key} sx={bodyCellSx}>
                      {renderLeaveCell(row, head)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
